import { type ReactNode, useEffect, useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { Logo, Avatar, Button, Badge } from '@yhelp/ui';
import { useAuth } from '@/auth';
import { useMe } from '@/hooks/useMe';
import { HelpFab } from '@/components/organisms/HelpFab/HelpFab';
import { useNotifications } from '@/hooks/useNotifications';
import { NAV_ICONS } from '@/lib/nav-icons';

const NAV = [
  { to: '/app', label: 'Início', tour: 'nav-home' },
  { to: '/tickets', label: 'Chamados', tour: 'nav-tickets' },
  { to: '/queues', label: 'Filas', tour: 'nav-queues' },
  { to: '/notifications', label: 'Notificações', tour: 'nav-notifications' },
  { to: '/dashboard', label: 'Dashboard', tour: 'nav-dashboard' },
  { to: '/kb', label: 'Base de conhecimento', tour: 'nav-kb' },
  { to: '/settings', label: 'Configurações', tour: 'nav-settings' },
] as const;

function breadcrumbLabel(pathname: string): string {
  if (pathname.startsWith('/kb/')) return 'Artigo';
  if (pathname.startsWith('/tickets/') && pathname !== '/tickets/new') return 'Detalhe do chamado';
  if (pathname === '/tickets/new') return 'Novo chamado';
  if (pathname === '/tickets') return 'Chamados';
  if (pathname === '/queues') return 'Filas';
  if (pathname === '/dashboard') return 'Dashboard';
  if (pathname === '/settings') return 'Configurações';
  if (pathname === '/notifications') return 'Notificações';
  if (pathname === '/kb') return 'Base de conhecimento';
  if (pathname === '/onboarding') return 'Boas-vindas';
  return 'Início';
}

function breadcrumbTrail(pathname: string, pageTitle?: string): string {
  if (pathname.startsWith('/kb/') && pageTitle) {
    return `Base de conhecimento / ${pageTitle}`;
  }
  return breadcrumbLabel(pathname);
}

export function DashboardLayout({
  children,
  primaryAction,
  pageDescription,
  pageTitle: pageTitleOverride,
}: {
  children: ReactNode;
  primaryAction?: ReactNode;
  pageDescription?: string;
  pageTitle?: string;
}) {
  const { user, logout } = useAuth();
  const { data: me } = useMe();
  const tenant = me?.data.tenant;
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const pageTitle = pageTitleOverride ?? breadcrumbLabel(pathname);
  const breadcrumb = breadcrumbTrail(pathname, pageTitleOverride);
  const { data: notifications } = useNotifications();
  const unreadCount = notifications?.meta.unread_count ?? 0;
  const isTicketDetail =
    pathname.startsWith('/tickets/') && pathname !== '/tickets/new' && pathname !== '/tickets';

  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  const sidebarContent = (
    <>
      <div
        className={`border-b border-neutral-800 ${collapsed ? 'flex justify-center p-3' : 'p-4'}`}
      >
        {tenant?.logo_url && !collapsed ? (
          <img
            src={tenant.logo_url}
            alt={tenant.name}
            className="h-8 max-w-full object-contain object-left"
          />
        ) : (
          <Logo variant="official" size="sm" />
        )}
        {!collapsed && tenant ? (
          <p className="mt-2 truncate text-xs text-neutral-400">{tenant.name}</p>
        ) : null}
      </div>
      <nav
        className="flex-1 space-y-1 overflow-y-auto overflow-x-hidden p-3"
        aria-label="Navegação principal"
      >
        {NAV.map((item) => {
          const active = pathname === item.to || pathname.startsWith(`${item.to}/`);
          const Icon = NAV_ICONS[item.to];
          return (
            <Link
              key={item.to}
              to={item.to}
              data-tour={item.tour}
              title={item.label}
              onClick={() => setMobileNavOpen(false)}
              className={`flex items-center rounded-lg text-sm font-medium transition-all duration-fast motion-reduce:transition-none ${
                collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2'
              } ${
                active
                  ? collapsed
                    ? 'bg-neutral-900 text-[var(--tenant-primary,#FFE600)] ring-2 ring-inset ring-[var(--tenant-primary,#FFE600)]'
                    : 'border-l-4 border-[var(--tenant-primary,#FFE600)] bg-neutral-900 pl-2 text-[var(--tenant-primary,#FFE600)]'
                  : 'text-neutral-300 hover:bg-neutral-900 hover:text-white'
              }`}
            >
              {Icon ? <Icon className="h-5 w-5 shrink-0" /> : null}
              {!collapsed ? <span className="min-w-0 truncate">{item.label}</span> : null}
            </Link>
          );
        })}
      </nav>
      <div className={`border-t border-neutral-800 ${collapsed ? 'p-2' : 'p-4'}`}>
        {!collapsed && (
          <div className="flex items-center gap-3 overflow-hidden">
            <Avatar name={user?.name ?? '?'} size="sm" />
            <div className="min-w-0 flex-1 overflow-hidden">
              <p className="truncate text-sm font-semibold">{user?.name}</p>
              <Badge variant="neutral" className="mt-1 max-w-full truncate normal-case">
                {user?.role}
              </Badge>
            </div>
          </div>
        )}
        <div className={`flex ${collapsed ? 'flex-col gap-1' : 'mt-3 flex-col gap-2'}`}>
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
            title={collapsed ? 'Expandir menu' : 'Recolher menu'}
            className={`rounded-md border border-neutral-700 text-neutral-300 transition-colors duration-fast hover:bg-neutral-900 motion-reduce:transition-none ${
              collapsed
                ? 'flex h-9 w-full items-center justify-center text-base'
                : 'w-full px-3 py-1.5 text-xs'
            }`}
          >
            {collapsed ? '»' : '« Recolher'}
          </button>
          <button
            type="button"
            onClick={() => void logout({ logoutParams: { returnTo: window.location.origin } })}
            aria-label="Sair"
            title="Sair"
            className={`rounded-md border border-neutral-700 text-neutral-300 transition-colors duration-fast hover:bg-neutral-900 motion-reduce:transition-none ${
              collapsed
                ? 'flex h-9 w-full items-center justify-center'
                : 'w-full px-3 py-1.5 text-sm'
            }`}
          >
            {collapsed ? (
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
            ) : (
              'Sair'
            )}
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-neutral-50 dark:bg-neutral-950">
      {mobileNavOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-yhelp-black/50 md:hidden"
          aria-label="Fechar menu"
          onClick={() => setMobileNavOpen(false)}
        />
      ) : null}

      <aside
        data-tour="sidebar"
        className={`fixed inset-y-0 left-0 z-50 flex flex-col overflow-hidden bg-yhelp-black text-white transition-all duration-slow motion-reduce:transition-none md:static md:z-auto md:translate-x-0 ${
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        } ${collapsed ? 'w-[4.25rem] md:w-[4.25rem]' : 'w-64'}`}
      >
        {sidebarContent}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-neutral-200 bg-white/90 px-4 py-3 backdrop-blur dark:border-neutral-800 dark:bg-neutral-900/90 sm:px-6 sm:py-4">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <button
              type="button"
              className="mt-0.5 rounded-lg p-2 text-neutral-600 hover:bg-neutral-100 md:hidden dark:text-neutral-300 dark:hover:bg-neutral-800"
              aria-label="Abrir menu"
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen(true)}
            >
              <svg
                viewBox="0 0 24 24"
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="min-w-0">
              <p className="truncate text-xs text-muted">yHelp / {breadcrumb}</p>
              <h1 className="truncate text-lg font-bold text-yhelp-black dark:text-white">
                {pageTitle}
              </h1>
              {pageDescription ? (
                <p className="mt-0.5 line-clamp-2 text-sm text-muted">{pageDescription}</p>
              ) : null}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <Link
              to="/notifications"
              className={`relative rounded-lg p-2 text-neutral-600 transition-colors duration-fast hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800 motion-reduce:transition-none ${
                unreadCount > 0 ? 'animate-pulse-soft motion-reduce:animate-none' : ''
              }`}
              aria-label={`Notificações${unreadCount ? `, ${unreadCount} não lidas` : ''}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="h-6 w-6"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              {unreadCount > 0 ? (
                <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-yhelp-yellow px-1 text-xs font-bold text-yhelp-black">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              ) : null}
            </Link>
            {primaryAction ? <div data-tour="page-primary-action">{primaryAction}</div> : null}
          </div>
        </header>
        <main className="flex-1 p-4 page-enter sm:p-6">{children}</main>
      </div>
      <HelpFab chatOpen={isTicketDetail} />
    </div>
  );
}

export function PrimaryLinkButton({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to}>
      <Button>{children}</Button>
    </Link>
  );
}
